import axios from 'axios'
import { Form, Formik } from 'formik'
import { isEmpty } from 'lodash'
import { useState } from 'react'
import { useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import Modal from 'rsuite/Modal'
import { API_URL } from 'src/cms/constants/EndPoint'
import FormInputMediaManager from 'src/cms/helpers/components/forms/FormInputMediaManager'
import FormSelect from 'src/cms/helpers/components/forms/FormSelect'
import FormTextBox from 'src/cms/helpers/components/forms/FormTextBox'
import FormTinyMce from 'src/cms/helpers/components/forms/FormTinyMce'
import { imageBaseUrl } from 'src/cms/helpers/constants'
import { RootState } from 'src/setup/redux/RootReducer'
import * as Yup from 'yup'
import { useGetProductSchemaById, useUpdateProductSchema } from './hooks/useGetProductSchema'
import { useGetDocuments, useGetDocumentsAll } from '../documents/hooks/useGetDocuments'

type Props = {
  open: boolean
  handleClose: () => void
  id: string
}

const AddProductSchema = ({ open, handleClose, id }: Props) => {
  const queryClient = useQueryClient()
  const [imagePath, setImagePath] = useState<any>('')
  const [isUpdating, setIsUpdating] = useState(false)
  const { data: productSchemaByIdData, refetch } = useGetProductSchemaById(id)
  const { data: documentsData } = useGetDocumentsAll()
  const allMasterData = useSelector(
    (state: RootState): any => state.allMasterDataOptions.allMasterDataOptions
  )

  const residentOption = [
    { label: 'Inside Country', value: 'inside' },
    { label: 'Outside Country', value: 'outside' },
    { label: 'Both', value: 'both' },
  ]
  const yesNoOptions = [
    { label: 'YES', value: 'Yes' },
    { label: 'NO', value: 'No' },
  ]

  const getDropdownOptions = (data: any) => {
    return data?.map((item: any) => ({
      label: item?.title,
      value: item?.id,
    }))
  }

  const FORM_VALIDATION = Yup.object({
    online_schema_title: Yup.string().required('Required'),
    gl_sub_head_code: Yup.string().required('Required'),
    interest_rate: Yup.string().required('Required'),
    minimum_balance: Yup.string().required('Required'),
    button_name: Yup.string().required('Required'),
    button_link: Yup.string().required('Required'),
  })

  // Map API data to form fields
  const getInitialValues = () => {
    const data = productSchemaByIdData?.data
    return {
      ...data,
      title: data?.title || '',
      online_schema_title: data?.online_schema_title || '',
      cbs_code: data?.cbs_code || '',
      gl_sub_head_code: data?.gl_sub_head_code || '',
      account_type_id: data?.account_type_id || '',
      account_categories: data?.account_categories || [],
      documents: data?.documents || [],
      interest_rate: data?.interest_rate ?? data?.tax_percentage ?? '',
      minimum_balance: data?.minimum_balance ?? '',
      button_name: data?.button_name ?? '',
      button_link: data?.button_link ?? '',
      is_nrn: data?.is_nrn ? 'Yes' : 'No',
      resident: data?.resident,
      show_in_website: data?.show_in_website ? 'Yes' : 'No',
      image_url: data?.image_url ?? '',
      description: data?.description || '',
      document_info: data?.document_info || '',
      // status: data?.status ? 'Active' : 'Inactive',
    }
  }

  async function handleSubmit(value: any) {
    setIsUpdating(true)
    const updateValue = {
      ...value,
      resident: value?.resident,
      is_nrn: value?.is_nrn === 'Yes' ? true : false,
      show_in_website: value?.show_in_website === 'Yes' ? true : false,
    }
    try {
      const responseData = await axios.put(
        `${API_URL}/product-scheme/update/${updateValue?.id}`,
        updateValue
      )
      if (responseData?.data?.data?.status) {
        queryClient.invalidateQueries('GET_PRODUCT_SCHEMA_BY_ID')
        queryClient.invalidateQueries('GET_PRODUCT_SCHEMA')
        setIsUpdating(false)
        handleClose()
        toast.success(responseData?.data?.data?.message || 'Product Schema updated successfully')
        refetch()
      }
    } catch (error) {
      setIsUpdating(false)
      toast.error((error as any)?.data?.data?.message || 'Something went wrong')
    }
  }

  return (
    <div className='modal-container'>
      <Modal
        open={open}
        onClose={handleClose}
        className='w-75'
        backdrop='static'
        enforceFocus={false}
        keyboard={false}
        data-cy='modal'
      >
        <Modal.Header>
          <Modal.Title data-cy='modal-title'>Product Schema </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ minHeight: '80vh' }}>
          <div>
            <Formik
              initialValues={getInitialValues()}
              validationSchema={FORM_VALIDATION}
              onSubmit={(value) => handleSubmit(value)}
              enableReinitialize
            >
              {({ touched, handleChange, errors, values, setFieldValue }) => {
                return (
                  <Form>
                    <div className='card-body border-top pt-5'>
                      <div className='row'>
                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            type='text'
                            placeholder='Enter Title'
                            name='title'
                            label='Title'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            value={values.title}
                            disabled={true}
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            type='text'
                            placeholder='Online Schema Title'
                            name='online_schema_title'
                            label='Online Schema Title'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            required={true}
                            value={values.online_schema_title}
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            type='text'
                            placeholder='CBS Code'
                            name='cbs_code'
                            label='CBS Code'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            value={values.cbs_code}
                            disabled={true}
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            type='text'
                            placeholder='GL Code'
                            name='gl_sub_head_code'
                            label='GL Code'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            required={true}
                            value={values.gl_sub_head_code}
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormSelect
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            options={getDropdownOptions(allMasterData?.account_types)}
                            name='account_type_id'
                            label='Account Type'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            values={values}
                            setFieldValue={setFieldValue}
                            disabled={true}
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormSelect
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            options={getDropdownOptions(allMasterData?.account_category)}
                            name='account_categories'
                            label='Account Category'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            values={values}
                            setFieldValue={setFieldValue}
                            multiple={true}
                            disabled={true}
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormSelect
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            options={getDropdownOptions(allMasterData?.document_types)}
                            name='documents'
                            label='Required Documents'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            values={values}
                            setFieldValue={setFieldValue}
                            multiple={true}
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            type='text'
                            label='Interest Rate'
                            name='interest_rate'
                            touched={touched}
                            errors={errors as any}
                            onChange={handleChange}
                            data-cy='subtitle-text-area-field'
                            required={true}
                            value={values.interest_rate}
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            type='text'
                            label='Minimum Balance'
                            name='minimum_balance'
                            placeholder='Enter Minimum Balance'
                            touched={touched}
                            errors={errors as any}
                            onChange={handleChange}
                            data-cy='subtitle-text-area-field'
                            required={true}
                            value={values.minimum_balance}
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            type='text'
                            label='Button Name'
                            name='button_name'
                            placeholder='Enter Button Name'
                            touched={touched}
                            errors={errors as any}
                            onChange={handleChange}
                            data-cy='subtitle-text-area-field'
                            required={true}
                            value={values.button_name}
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            type='text'
                            label='Button Link'
                            name='button_link'
                            placeholder='Enter Button Link'
                            touched={touched}
                            errors={errors as any}
                            onChange={handleChange}
                            data-cy='subtitle-text-area-field'
                            required={true}
                            value={values.button_link}
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormSelect
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            options={yesNoOptions}
                            name='is_nrn'
                            label='Is NR'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            values={values}
                            setFieldValue={setFieldValue}
                            required={true}
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormSelect
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            options={residentOption}
                            name='resident'
                            label='Resident'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            values={values}
                            setFieldValue={setFieldValue}
                            required={true}
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormSelect
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            options={yesNoOptions}
                            name='show_in_website'
                            label='Show in Online Account Opening'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            values={values}
                            setFieldValue={setFieldValue}
                            required={true}
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormInputMediaManager
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            label=' Image '
                            name='image_url'
                            setFieldValue={setFieldValue}
                            setImageUrl={setImagePath}
                            value={values?.image_url}
                            required={false}
                          />

                          {!isEmpty(values?.image_url) ? (
                            <>
                              <li className='listing'>
                                <div className='thumbImageBlock'>
                                  <button
                                    type='button'
                                    title='Remove'
                                    className='btn thumbImage-remove d-flex align-items-center justify-content-center btn-primary'
                                    onClick={() => {
                                      setImagePath('')
                                      setFieldValue('image_url', '')
                                    }}
                                    data-cy='modal-thumbnail-remove'
                                  >
                                    X
                                  </button>

                                  <img
                                    className='thumbImage w-100 h-100'
                                    src={`${imageBaseUrl}/${values?.image_url}`}
                                    alt=''
                                  />
                                </div>
                              </li>
                            </>
                          ) : null}
                        </div>
                      </div>
                      <div className='row'>
                        <div className='col-md-12 col-xs-12'>
                          <FormTinyMce
                            containerClassName='col-md-12'
                            labelClassName='col-md-12'
                            label='Document Information (Outside)'
                            name='description'
                            initialValue={
                              !isEmpty(productSchemaByIdData?.data?.description)
                                ? productSchemaByIdData?.data?.description
                                : ''
                            }
                            handleChange={handleChange}
                            required={true}
                          />
                        </div>
                      </div>
                      <div className='row'>
                        <div className='col-md-12 col-xs-12'>
                          <FormTinyMce
                            containerClassName='col-md-12'
                            labelClassName='col-md-12'
                            label='Document Information (Inside)'
                            name='document_info'
                            initialValue={
                              !isEmpty(productSchemaByIdData?.data?.document_info)
                                ? productSchemaByIdData?.data?.document_info
                                : ''
                            }
                            handleChange={handleChange}
                            required={true}
                          />
                        </div>
                      </div>
                      {/* <div className='row'>
                        <div className='col-md-6 col-xs-12'>
                          <label className='form-label fw-bolder text-dark fs-6 required'>
                            Status
                          </label>
                          <div className='d-flex ms-5'>
                            {statusOptions?.map((status: {label: string; value: string}) => (
                              <FormRadio
                                key={status.value}
                                containerClassName=''
                                label='Select Status'
                                name='status'
                                type='radio'
                                onChange={handleChange}
                                errors={errors}
                                touched={touched}
                                required={true}
                                checkBoxText={status?.label}
                                value={status?.value}
                                checked={values.status === status.value}
                                data-cy='product-category-status-radio'
                              />
                            ))}
                          </div>
                          <div className='fv-plugins-message-container text-danger fw-bolder small text-end mt-1'>
                            <ErrorMessage
                              name='status'
                              component='div'
                              className='field-error-message'
                            />
                          </div>
                        </div>
                      </div> */}
                    </div>
                    <div className='d-flex justify-content-end px-9 '>
                      <button
                        type='submit'
                        disabled={isUpdating}
                        className='btn btn-primary btn-sm ms-3'
                        data-cy='modal-submit'
                      >
                        {isUpdating ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        type='button'
                        onClick={handleClose}
                        className='btn btn-secondary btn-sm ms-3'
                        data-cy='modal-cancel'
                      >
                        Cancel
                      </button>
                    </div>
                  </Form>
                )
              }}
            </Formik>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  )
}
export default AddProductSchema
